// Get token from localStorage
const token = localStorage.getItem('token');

export const getAutodeskVaultCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // Find the Autodesk Vault master connector details
    const autodeskVaultConnector = masterConnectors.find(mc => 
      (mc.mcRecId?.toString() === mcId?.toString() || 
       mc.mcId?.toString() === mcId?.toString()) && 
      mc.mcName === "Autodesk Vault"
    );
    if (!autodeskVaultConnector) {
      throw new Error('Autodesk Vault master connector not found');
    }

    // Call the createSession endpoint directly (no OAuth redirect needed)
    const sessionResponse = await createAutodeskVaultSession(connectorId);
    
    if (sessionResponse) {
      // Update the connector with session data
      await updateConnectorWithSessionData(connectorId, sessionResponse);
      return sessionResponse;
    } else {
      throw new Error('Failed to create Autodesk Vault session');
    }
    
  } catch (error) {
    console.error('Error getting Autodesk Vault credentials:', error);
    throw error;
  }
};

// Helper function to create Autodesk Vault session
const createAutodeskVaultSession = async (connectorId) => {
  try {
    const sessionUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/autodesk/vault/createSession?mcId=${connectorId}`;
    
    const response = await fetch(sessionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Session creation error response:', errorText);
      throw new Error(`Failed to create Vault session: ${response.status}`);
    }

    const sessionData = await response.json();
    console.log('Vault session created successfully:', sessionData);
    return sessionData;
    
  } catch (error) {
    console.error('Vault session creation error:', error);
    throw error;
  }
};

// Helper function to update connector with session data
const updateConnectorWithSessionData = async (connectorId, sessionData) => {
  try {
    const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
    
    // First, get the current connector data
    const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!connectorResponse.ok) {
      throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
    }
    const connectorData = await connectorResponse.json();
    
    // Calculate session expiry to match backend (45 minutes)
    const SESSION_VALIDITY_MS = 45 * 60 * 1000; // 45 minutes in milliseconds
    const sessionExpiry = new Date(Date.now() + SESSION_VALIDITY_MS).toISOString();
    
    // Prepare updated connector data - align with backend storage pattern
    const updatedConnectorData = {
      ...connectorData,
      token: sessionData.accessToken || sessionData.id, // Use accessToken or session ID as token
      refreshToken: sessionData.id, // Store session ID in refreshToken field (matching backend)
      tokenExpiry: sessionExpiry,
      status: 'Connected',
      connectedTime: new Date().toISOString(),
      // Store additional session info if available
      ...(sessionData.url && { sessionUrl: sessionData.url })
    };
    
    // Make the PUT request to update the connector
    const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updatedConnectorData)
    });
    
    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      console.error('Update connector error:', errorText);
      throw new Error(`Failed to update connector with session data: ${updateResponse.status}`);
    }
    
    console.log('Autodesk Vault connector updated successfully with session data');
    return true;
  } catch (error) {
    console.error('Error updating Autodesk Vault connector with session data:', error);
    throw error;
  }
};

// No separate exchange function needed since we create session directly
export const exchangeAutodeskVaultAuthCodeForToken = async (authCode, clientId, clientSecret, mcId) => {
  // This function is not needed for Vault since it doesn't use OAuth
  // Keeping it for compatibility with the existing structure
  throw new Error('Autodesk Vault does not use OAuth authentication');
};